import { useState, useRef, useEffect, useCallback } from "react";

interface UseResizableSidebarOptions {
  sidebarKey: string;
  minWidth?: number;
  maxWidth?: number;
  direction: "left" | "right"; // New parameter to specify the sidebar direction
}

export const useResizableSidebar = ({
  sidebarKey,
  minWidth = 200,
  maxWidth = 600,
  direction,
}: UseResizableSidebarOptions) => {
  const [width, setWidth] = useState(() => {
    const storedWidth = localStorage.getItem(sidebarKey);
    return storedWidth ? parseInt(storedWidth, 10) : minWidth;
  });

  const isResizing = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      isResizing.current = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizing.current) return;

        const newWidth =
          direction === "left"
            ? Math.min(Math.max(moveEvent.clientX, minWidth), maxWidth) // Left sidebar: width grows as the mouse moves right
            : Math.min(
                Math.max(window.innerWidth - moveEvent.clientX, minWidth),
                maxWidth
              ); // Right sidebar: width grows as the mouse moves left

        setWidth(newWidth);
      };

      const handleMouseUp = () => {
        isResizing.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [direction, minWidth, maxWidth]
  );

  useEffect(() => {
    localStorage.setItem(sidebarKey, width.toString());
  }, [width, sidebarKey]);

  return { width, handleMouseDown };
};
